import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Field, Form, Formik } from 'formik';
import { debounce, get } from 'lodash';
import { Grid } from '@material-ui/core';
import { object, string } from 'yup';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../SubmitModalLayout';
import { dateFormats, TP } from '../../constants/consts';
import CountryCityBlock from '../CountryCityBlock';
import EditModeCard from '../EditModeCard';
import FormikTextField from '../Fields/TextField';
import FormikAutocompleteSelect from '../Fields/FormikAutocompleteSelect';
import TextArea from '../Fields/TextArea';
import FormikPhoneField from '../Fields/FormikPhoneField';
import {
  createEntity,
  getCompaniesAction,
  getVenuesAction,
  getWholeList,
  useBufferValueAction,
} from '../../containers/App/actions';
import { FESTIVALS, VENUES } from '../../constants/endpointConstants';
import {
  selectBufferValueData,
  selectCompaniesListOptions,
  selectCompaniesListOptionsLoading,
  selectVenuesOptions,
} from '../../containers/App/selectors';
import CalendarField from '../Fields/CalendarField';
import AddNewCompanyModal from '../AddNewCompanyModal';
import { getOption } from '../../utils/common';
import NoOptionsBox from '../NoOptionsBox';
import Button from '../Button';
import { createDate } from '../../utils/date';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
  },
  fieldsBox: {
    padding: theme.spacing(2),
    margin: 0,
  },
}));

const companyFields = {
  name: {
    name: 'name',
    label: `${TP}.FN_FESTIVAL_NAME`,
  },
  company: {
    name: 'company',
    label: `${TP}.FESTIVAL_ORGANISER`,
  },
  country: {
    name: 'country',
  },
  city: {
    name: 'city',
  },
  venue: {
    name: 'venue',
    label: `${TP}.VENUE_NAME`,
  },
  address: {
    name: 'address',
    label: `${TP}.m_ADDRESS`,
  },
  email: {
    name: 'email',
    label: `${TP}.m_EMAIL`,
  },
  phone: {
    name: 'phone',
    label: `${TP}.m_REGISTRATION_PHONENUMBER`,
  },
  startDate: {
    name: 'startDate',
    label: `${TP}.m_FROM`,
  },
  endDate: {
    name: 'endDate',
    label: `${TP}.m_TO`,
  },
};

const { name, company, country, city, venue, address, email, phone, startDate, endDate } = companyFields;

const initialValues = festivalName => ({
  [name.name]: festivalName,
  [company.name]: '',
  [country.name]: undefined,
  [city.name]: undefined,
  [venue.name]: undefined,
  [address.name]: '',
  [email.name]: '',
  [phone.name]: '',
  [startDate.name]: '',
  [endDate.name]: '',
});

const AddNewFestivalModal = ({
  className,
  onClose,
  festivalName,
  venues,
  companies,
  companiesLoading,
  createdFestival,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);

  const [newCompanyOpened, setNewCompanyState] = React.useState(false);
  const [newCompanyName, setNewCompanyName] = React.useState('');
  const [companyStartSearching, setCompanySearcing] = React.useState(false);

  const openAddNewCompanyModel = () => {
    setNewCompanyState(true);
  };

  const closeAddNewCompanyModal = createdCompany => {
    if (createdCompany?.id) {
      get(formRef, 'current.setFieldValue', () => {})(company.name, getOption(createdCompany));
    }
    setNewCompanyState(false);
    setNewCompanyName('');
  };

  const getVenues = debounce(cityId => {
    dispatch(
      getWholeList({
        endpoint: VENUES,
        sagaRoutine: getVenuesAction,
        params: {
          queryParams: {
            city_id: cityId,
          },
        },
      }),
    );
  }, 300);
  const fetchCompanies = debounce(query => dispatch(getCompaniesAction({ query: { query } })), 300);

  const getCompanies = query => {
    setCompanySearcing(true);
    setNewCompanyName(query);
    fetchCompanies(query);
  };

  React.useEffect(() => {
    setCompanySearcing(false);
  }, [companies]);

  const createFestival = values => {
    const phoneNumber = get(values, phone.name, '');
    const getIdFromValue = field => (get(values, `${field}.value`) ? { id: get(values, `${field}.value`) } : null);
    dispatch(
      createEntity({
        endpoint: FESTIVALS,
        sagaRoutine: useBufferValueAction,
        params: {
          name: get(values, name.name),
          company: getIdFromValue(company.name),
          country: getIdFromValue(country.name),
          city: getIdFromValue(city.name),
          venue: getIdFromValue(venue.name),
          address: get(values, address.name),
          contacts: {
            email: get(values, email.name),
            phoneNumber,
          },
          min_date: get(values, startDate.name) || null,
          max_date: get(values, endDate.name) || null,
        },
        callback: festival => onClose(festival),
      }),
    );
  };

  return (
    <>
      <SubmitModalLayout
        className={className}
        isOpened
        onClose={onClose}
        title1={t(`${TP}.FN_ADD_NEW_FESTIVAL`)}
        submitFunc={() => {
          get(formRef, 'current.submitForm', () => {})();
        }}
        isLoading={createdFestival?.loading}
      >
        <EditModeCard className={classes.form}>
          <Formik
            initialValues={initialValues(festivalName)}
            onSubmit={createFestival}
            validationSchema={AddNewFestivalSchema}
            innerRef={formRef}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Grid container spacing={2} className={classes.fieldsBox}>
                  <Grid item sm={6} xs={12}>
                    <Field name={name.name} component={FormikTextField} label={t(name.label)} />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Field
                      instanceId={company.name}
                      name={company.name}
                      component={FormikAutocompleteSelect}
                      label={t(company.label)}
                      options={companies}
                      isLoading={companiesLoading}
                      onInputTextChange={getCompanies}
                      onFocus={getCompanies}
                      startAggregations={companyStartSearching}
                      isClearable
                      customComponents={{
                        NoOptionsMessage: () => (
                          <NoOptionsBox
                            buttonText={`${TP}.FN_ADD_NEW_COMPANY`}
                            openFormFunc={openAddNewCompanyModel}
                            t={t}
                          />
                        ),
                      }}
                      staticOption={<Button onClick={openAddNewCompanyModel}>{t(`${TP}.FN_ADD_NEW_COMPANY`)}</Button>}
                    />
                  </Grid>
                  {[startDate, endDate].map(date => (
                    <Grid item sm={6} xs={12} key={date?.name}>
                      <Field
                        name={date.name}
                        component={CalendarField}
                        label={t(date.label)}
                        onChange={day => setFieldValue(date.name, day ? createDate(day).format(dateFormats.basic) : '')}
                        disabledDays={[
                          {
                            before:
                              date?.name === endDate?.name && get(values, startDate?.name)
                                ? new Date(get(values, startDate?.name))
                                : undefined,
                          },
                        ]}
                      />
                    </Grid>
                  ))}
                  <CountryCityBlock
                    formValues={values}
                    setFieldValue={setFieldValue}
                    onCityChange={v => {
                      setFieldValue(venue.name, null);
                      if (v?.value) {
                        getVenues(v?.value);
                      }
                    }}
                    isCreatable
                  />
                  <Grid item sm={6} xs={12}>
                    <Field
                      instanceId={venue.name}
                      name={venue.name}
                      component={FormikAutocompleteSelect}
                      label={t(venue.label)}
                      options={venues?.options || []}
                      isDisabled={!get(values, city.name, null)}
                      isClearable
                    />
                  </Grid>
                  <Grid item sm={12} xs={12}>
                    <Field name={address.name} component={TextArea} label={t(address.label)} />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Field name={email.name} component={FormikTextField} label={t(email.label)} />
                  </Grid>
                  <Grid item sm={6} xs={12} style={{ alignSelf: 'flex-start' }}>
                    <Field name={phone.name} label={t(phone.label)} component={FormikPhoneField} />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </EditModeCard>
      </SubmitModalLayout>
      {newCompanyOpened && <AddNewCompanyModal onClose={closeAddNewCompanyModal} companyName={newCompanyName || ''} />}
    </>
  );
};

AddNewFestivalModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  festivalName: PropTypes.string,
};

AddNewFestivalModal.defaultProps = {
  festivalName: '',
};

const mapStateToProps = createStructuredSelector({
  venues: selectVenuesOptions(),
  companies: selectCompaniesListOptions(),
  companiesLoading: selectCompaniesListOptionsLoading(),
  createdFestival: selectBufferValueData(),
});
const withConnect = connect(mapStateToProps, null);

export default withConnect(AddNewFestivalModal);

const AddNewFestivalSchema = () =>
  object().shape({
    [name.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [country.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [email.name]: string()
      .nullable()
      .email(`${TP}.m_MAILNONVALID`),
  });
